
/**
 * Utilities for location data and processing
 */

// Function to parse coordinates from address
export const parseCoordinatesFromAddress = (address: string | null): { lat: number, lng: number } => {
  if (!address) return { lat: 51.1657, lng: 10.4515 }; // Default coordinates for Germany
  
  try {
    // Try to extract coordinates from the full_address field
    // Sometimes Google Maps puts coordinates in the address field in formats like:
    // "coordinates: (52.5200, 13.4050)" or "location: 52.5200,13.4050" or similar
    const coordsRegex = /(\d+\.\d+),\s*(\d+\.\d+)/;
    const match = address.match(coordsRegex);
    
    if (match && match.length >= 3) {
      const lat = parseFloat(match[1]);
      const lng = parseFloat(match[2]);
      
      // Validate that the coordinates are valid (not 0,0 and within reasonable range)
      if (!isNaN(lat) && !isNaN(lng) && 
          lat !== 0 && lng !== 0 && 
          lat > -90 && lat < 90 && 
          lng > -180 && lng < 180) {
        console.log(`Extracted coordinates: ${lat}, ${lng} from address: ${address}`);
        return { lat, lng };
      }
    }
  } catch (e) {
    console.error("Error parsing coordinates from address:", e);
  }
  
  // Return default coordinates for Germany if unable to extract coords
  return { lat: 51.1657, lng: 10.4515 };
};

// Function to determine state from postal code with improved mappings
export const determineStateFromPostalCode = (postalCode: string | null, country: string): string => {
  if (!postalCode) return "";
  
  // Germany postal code mapping
  if (country === "Germany" || country.includes("Germany") || country.includes("Deutschland")) {
    const prefix = postalCode.substring(0, 2);
    const prefixNum = parseInt(prefix, 10);
    
    // Improved postal code mappings with special attention to edge cases
    if (prefixNum >= 1 && prefixNum <= 19) {
      if (prefixNum >= 1 && prefixNum <= 14) return "Berlin";
      if (prefixNum >= 15 && prefixNum <= 16) return "Brandenburg";
      if (prefixNum >= 17 && prefixNum <= 19) return "Mecklenburg-Vorpommern";
    }
    
    if (prefixNum >= 20 && prefixNum <= 29) {
      if (prefixNum >= 20 && prefixNum <= 21) return "Hamburg";
      if (prefixNum >= 22 && prefixNum <= 25) return "Hamburg";
      if (prefixNum >= 26 && prefixNum <= 27) return "Bremen";
      if (prefixNum >= 28 && prefixNum <= 29) return "Niedersachsen";
    }
    
    if (prefixNum >= 30 && prefixNum <= 39) {
      if (prefixNum >= 30 && prefixNum <= 31) return "Niedersachsen";
      if (prefixNum >= 32 && prefixNum <= 33) return "Nordrhein-Westfalen";
      if (prefixNum === 34) return "Hessen";
      if (prefixNum === 35) return "Nordrhein-Westfalen";
      if (prefixNum >= 36 && prefixNum <= 37) return "Hessen";
      if (prefixNum >= 38 && prefixNum <= 39) return "Sachsen-Anhalt"; // Explicitly correct
    }
    
    if (prefixNum >= 40 && prefixNum <= 49) {
      if (prefixNum >= 40 && prefixNum <= 48) return "Nordrhein-Westfalen";
      if (prefixNum === 49) return "Niedersachsen";
    }
    
    if (prefixNum >= 50 && prefixNum <= 59) {
      if (prefixNum >= 50 && prefixNum <= 53) return "Nordrhein-Westfalen";
      if (prefixNum === 54) return "Rheinland-Pfalz";
      if (prefixNum >= 55 && prefixNum <= 56) return "Rheinland-Pfalz";
      if (prefixNum === 57) return "Nordrhein-Westfalen";
      if (prefixNum >= 58 && prefixNum <= 59) return "Nordrhein-Westfalen";
    }
    
    if (prefixNum >= 60 && prefixNum <= 69) {
      if (prefixNum >= 60 && prefixNum <= 65) return "Hessen";
      if (prefixNum >= 66 && prefixNum <= 67) return "Saarland";
      if (prefixNum >= 68 && prefixNum <= 69) return "Baden-Württemberg";
    }
    
    if (prefixNum >= 70 && prefixNum <= 79) return "Baden-Württemberg";
    
    if (prefixNum >= 80 && prefixNum <= 89) return "Bayern";
    
    if (prefixNum >= 90 && prefixNum <= 99) {
      if (prefixNum >= 90 && prefixNum <= 97) return "Bayern";
      if (prefixNum === 98) return "Thüringen";
      if (prefixNum === 99) return "Thüringen";
    }
    
    if (prefixNum >= 0 && prefixNum <= 9) {
      if (prefixNum === 0) return "Sachsen"; // Added for postal codes starting with 0
      if (prefixNum >= 1 && prefixNum <= 9) return "Sachsen";
    }
    
    // Default to empty if no match
    return "";
  }
  
  // Austria postal code mapping
  if (country === "Austria" || country.includes("Austria") || country.includes("Österreich")) {
    const prefix = postalCode.substring(0, 1);
    
    if (prefix === "1") return "Wien";
    if (prefix === "2") return "Niederösterreich";
    if (prefix === "3") return "Niederösterreich";
    if (prefix === "4") return "Oberösterreich";
    if (prefix === "5") return "Salzburg";
    if (prefix === "6") return "Tirol";
    if (prefix === "7") return "Burgenland";
    if (prefix === "8") return "Steiermark";
    if (prefix === "9") return "Kärnten";
    // Default to empty if no match
    return "";
  }
  
  // Switzerland postal code mapping (simplified)
  if (country === "Switzerland" || country.includes("Switzerland") || country.includes("Schweiz")) {
    const prefix = postalCode.substring(0, 2);
    const prefixNum = parseInt(prefix, 10);
    
    if (prefixNum >= 10 && prefixNum <= 12) return "Genf";
    if (prefixNum >= 13 && prefixNum <= 15) return "Waadt";
    if (prefixNum >= 16 && prefixNum <= 19) return "Wallis";
    if (prefixNum >= 20 && prefixNum <= 29) return "Neuenburg";
    if (prefixNum >= 30 && prefixNum <= 34) return "Bern";
    if (prefixNum >= 35 && prefixNum <= 36) return "Jura";
    if (prefixNum >= 40 && prefixNum <= 41) return "Neuenburg";
    if (prefixNum >= 45 && prefixNum <= 48) return "Waadt";
    if (prefixNum >= 49 && prefixNum <= 49) return "Freiburg";
    if (prefixNum >= 50 && prefixNum <= 52) return "Bern";
    if (prefixNum >= 53 && prefixNum <= 56) return "Freiburg";
    if (prefixNum >= 60 && prefixNum <= 65) return "Bern";
    if (prefixNum >= 66 && prefixNum <= 67) return "Solothurn";
    if (prefixNum >= 68 && prefixNum <= 69) return "Basel-Stadt";
    if (prefixNum >= 70 && prefixNum <= 71) return "Basel-Landschaft";
    if (prefixNum >= 72 && prefixNum <= 73) return "Solothurn";
    if (prefixNum >= 80 && prefixNum <= 89) return "Zürich";
    if (prefixNum >= 90 && prefixNum <= 96) return "St. Gallen";
    if (prefixNum >= 97 && prefixNum <= 97) return "Lichtenstein";
    return "";
  }
  
  return "";
};
